// frontend/src/pages/Trips.js
// 🧳 Utazás-alapú használat — a klasszikus havi retention helyett.
//
// Ez az oldal azt a kérdést szolgálja ki, hogy egy EPISODIKUS terméknél mi a
// jó mérőszám. A havi pontbeli retention utazási appnál félrevezető: aki
// februárban utazik és augusztusban újra, az minden hónapban 0 — pedig
// visszatérő felhasználó.
import React, { useEffect, useState } from 'react';

const C = {
  panel: '#1a1d27', panel2: '#22252f', border: '#2a2e3a',
  text: '#e4e6eb', muted: '#8b8fa3', accent: '#00d4ff',
  ok: '#22c55e', warn: '#fbbf24', bad: '#ef4444', purple: '#a855f7',
};
const card = { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 16 };
const h3 = { fontSize: 13, color: C.muted, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 };
const th = { padding: '6px 8px', textAlign: 'left', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', borderBottom: '1px solid #20242e' };
const num = (v, u) => (v == null ? '—' : `${typeof v === 'number' ? v.toLocaleString('hu-HU') : v}${u ? ' ' + u : ''}`);

function Kpi({ label, value, unit, sub, color = C.accent }) {
  return (
    <div style={{ ...card, marginBottom: 0 }}>
      <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color }}>{num(value, unit)}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function Trips() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gapDays, setGapDays] = useState(5);
  const [jumpKm, setJumpKm] = useState(100);

  const load = (refresh) => {
    setLoading(true); setErr(null);
    fetch(`/api/trips/summary?gapDays=${gapDays}&jumpKm=${jumpKm}${refresh ? '&refresh=1' : ''}`, { credentials: 'include' })
      .then(async (r) => { if (!r.ok) throw new Error((await r.json()).error || 'HTTP ' + r.status); return r.json(); })
      .then(setD).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  };
  // A küszöbök változásakor újraszámolunk. A `load` szándékosan nincs a
  //   dependency listában: minden rendereléskor új referencia lenne, és
  //   végtelen ciklust okozna.
  useEffect(() => { load(false); }, [gapDays, jumpKm]); // eslint-disable-line

  if (loading && !d) return <div style={{ padding: 24, color: C.muted }}>Számolás… (teljes pásztázás, eltarthat egy percig)</div>;
  if (err) return <div style={{ padding: 24, color: C.bad }}>Hiba: {err}</div>;
  if (!d) return null;

  const T = d.trips, S = d.sensitivity;

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: C.text }}>🧳 Utazás-alapú használat</h1>
        <button onClick={() => load(true)} disabled={loading}
          style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.panel2, color: C.text, cursor: loading ? 'default' : 'pointer', fontSize: 13 }}>
          {loading ? '⏳ Számolás…' : '↻ Újraszámolás'}
        </button>
      </div>
      <div style={{ color: C.muted, fontSize: 13, margin: '8px 0 16px', lineHeight: 1.6 }}>
        Egy utazási appnál a havi pontbeli retention félrevezető: aki februárban utazik és augusztusban újra,
        az minden naptári hónapban 0 — pedig visszatérő. Ez az oldal <b style={{ color: C.text }}>utazásokban</b> mér.
        Állapot: <b style={{ color: C.text }}>{d.asOfDate}</b>.
      </div>

      {/* A négy fő mutató */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Kpi label="Visszatér a 2. útra" value={T.reactivationPct} unit="%"
          sub={`${num(T.usersWith2PlusTrips)} / ${num(T.usersWithTrips)} felhasználó`} color={C.ok} />
        <Kpi label="Narráció / út (medián)" value={T.narrationsPerTripMedian} unit="db"
          sub={`átlag ${num(T.narrationsPerTripMean)}`} />
        <Kpi label="Út hossza (medián)" value={T.tripDurationDaysMedian} unit="nap"
          sub={`átlag ${num(T.tripDurationDaysMean)} nap`} color={C.warn} />
        <Kpi label="Város / út (medián)" value={T.citiesPerTripMedian} unit="db"
          sub={`POI/út: ${num(T.poisPerTripMedian)}`} color={C.purple} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        <div style={card}>
          <div style={h3}>Utazások</div>
          <div style={{ fontSize: 13, color: C.muted, lineHeight: 2 }}>
            Összes utazás: <b style={{ color: C.text }}>{num(T.totalTrips)}</b><br />
            Utazás / felhasználó: <b style={{ color: C.text }}>{num(T.tripsPerUserMean)}</b> átlag, <b style={{ color: C.text }}>{num(T.tripsPerUserMedian)}</b> medián<br />
            Harmadik útra is visszatér: <b style={{ color: C.text }}>{num(T.thirdTripPct, '%')}</b> ({num(T.usersWith3PlusTrips)} fő)<br />
            Utak közti idő (medián): <b style={{ color: C.text }}>{num(T.daysBetweenTripsMedian, 'nap')}</b>
          </div>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.muted }}>
            Beolvasott tranzakció: {num(d.input.scannedTransactions)} · narrációt használó user: {num(d.input.usersWithNarrations)}
            {d.input.skippedNoTime > 0 && <> · időbélyeg nélkül kihagyva: {num(d.input.skippedNoTime)}</>}
            {d.input.skippedNoUser > 0 && <> · userId nélkül kihagyva: {num(d.input.skippedNoUser)}</>}
          </div>
        </div>

        {/* Küszöb-érzékenység */}
        <div style={{ ...card, borderColor: S.fragile ? C.warn : C.border }}>
          <div style={{ ...h3, color: S.fragile ? C.warn : C.muted }}>
            Küszöb-érzékenység {S.fragile ? '— ⚠️ törékeny' : '— stabil'}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>Szünet</th><th style={th}>Ugrás</th>
                <th style={{ ...th, textAlign: 'right' }}>Utak</th>
                <th style={{ ...th, textAlign: 'right' }}>Visszatérés</th>
              </tr></thead>
              <tbody>
                {S.rows.map((r) => (
                  <tr key={`${r.gapDays}-${r.jumpKm}`}
                    style={{ background: r.gapDays === gapDays && r.jumpKm === jumpKm ? '#1f2a33' : 'transparent' }}>
                    <td style={td}>{r.gapDays} nap</td>
                    <td style={td}>{r.jumpKm} km</td>
                    <td style={{ ...td, textAlign: 'right' }}>{num(r.totalTrips)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{num(r.reactivationPct, '%')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: S.fragile ? C.warn : C.muted, marginTop: 10, lineHeight: 1.6 }}>
            Szórás a visszatérési arányban: <b>{num(S.reactivationSpreadPp)} százalékpont</b>.
            {S.fragile
              ? ' Ez nagy — az „utazás" definíciója törékeny, tehát a számot ne közöld egyetlen küszöbbel anélkül, hogy a sávot is megmutatnád.'
              : ' Ez kicsi — az eredmény nem a küszöbválasztáson áll, tehát közölhető.'}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: C.muted }}>Küszöb:</span>
            {[3, 5, 7, 10].map((g) => (
              <button key={g} onClick={() => setGapDays(g)}
                style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                  border: `1px solid ${gapDays === g ? C.accent : C.border}`,
                  background: gapDays === g ? C.accent : C.panel2, color: gapDays === g ? '#0a0d14' : C.text }}>
                {g} nap
              </button>
            ))}
            {[50, 100, 200].map((k) => (
              <button key={k} onClick={() => setJumpKm(k)}
                style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                  border: `1px solid ${jumpKm === k ? C.purple : C.border}`,
                  background: jumpKm === k ? C.purple : C.panel2, color: jumpKm === k ? '#0a0d14' : C.text }}>
                {k} km
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Kumulatív megtartás */}
      <div style={card}>
        <div style={h3}>Kumulatív megtartás — „visszatért-e legalább egyszer"</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', minWidth: 620 }}>
            <thead><tr>
              <th style={th}>Kohorsz</th>
              <th style={{ ...th, textAlign: 'right' }}>Méret</th>
              <th style={{ ...th, textAlign: 'right' }}>Eltelt</th>
              <th style={{ ...th, textAlign: 'right' }}>Bármikor később</th>
              <th style={{ ...th, textAlign: 'right' }}>3 hónapon belül</th>
              <th style={{ ...th, textAlign: 'right' }}>6 hónapon belül</th>
              <th style={{ ...th, textAlign: 'right' }}>12 hónapon belül</th>
            </tr></thead>
            <tbody>
              {d.retention.map((r) => (
                <tr key={r.cohort}>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{r.cohort}</td>
                  <td style={{ ...td, textAlign: 'right', color: r.size < 20 ? C.warn : C.text }}>
                    {r.size}{r.size < 20 && ' ⚠️'}
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: C.muted }}>{r.monthsElapsed} hó</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: r.anyLaterPct >= 20 ? C.ok : C.text }}>
                    {num(r.anyLaterPct, '%')}
                  </td>
                  {['w3', 'w6', 'w12'].map((w) => (
                    <td key={w} style={{ ...td, textAlign: 'right', color: r[w + 'Observable'] ? C.text : C.muted }}>
                      {r[w + 'Observable'] ? num(r[w + 'Pct'], '%') : <span title="Az ablak még nem telt el — ez nem nulla megtartás, hanem hiányzó idő">nincs még adat</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 10, lineHeight: 1.7 }}>
          A ⚠️ a <b>20 fő alatti kohorszokat</b> jelöli: ott egyetlen ember is nagy százalékot mozdít,
          tehát a szám zaj, nem jelzés. A „nincs még adat" azt jelenti, hogy az ablak nem telt el —
          <b> nem</b> azt, hogy nulla a megtartás.
        </div>
      </div>

      {/* Amit nem lehet elhallgatni */}
      <div style={{ ...card, borderColor: C.warn }}>
        <div style={{ ...h3, color: C.warn, marginBottom: 8 }}>⚠️ Amit egy befektetőnek is el kell mondani</div>
        <ul style={{ margin: 0, paddingLeft: 20, color: C.muted, fontSize: 12, lineHeight: 1.8 }}>
          {d.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      </div>

      <div style={{ height: 40 }} />
    </div>
  );
}
